import type { Job } from './api';

export interface Stage {
  id: string;
  label: string;
}

/** Papermill's pipeline, in the words a demo audience reads. */
export const STAGES: Stage[] = [
  { id: 'RECEIVED', label: 'Received' },
  { id: 'CANONICALIZING', label: 'Cleaning up the document' },
  { id: 'TRIAGING', label: 'Planning each page' },
  { id: 'EXTRACTING', label: 'Reading the text' },
  { id: 'DETECTING', label: 'Spotting signatures' },
  { id: 'ATTRIBUTING', label: 'Finding entities and links' },
];

/** A recording's pipeline: Stylus transcribes, then Papermill reads the transcript. */
export const AUDIO_STAGES: Stage[] = [
  { id: 'MEASURING', label: 'Measuring the recording' },
  { id: 'NORMALIZING', label: 'Cleaning up the audio' },
  { id: 'TRANSCRIBING', label: 'Transcribing the speech' },
  { id: 'READING', label: 'Reading the transcript' },
  { id: 'ATTRIBUTING', label: 'Finding entities and links' },
];

/** A live session: recorded as it is streamed, then transcribed properly, then read. */
export const LIVE_STAGES: Stage[] = [
  { id: 'STREAMING', label: 'Recording live' },
  { id: 'ASSEMBLING', label: 'Assembling the recording' },
  { id: 'NORMALIZING', label: 'Cleaning up the audio' },
  { id: 'TRANSCRIBING', label: 'Transcribing the speech' },
  { id: 'READING', label: 'Reading the transcript' },
  { id: 'ATTRIBUTING', label: 'Finding entities and links' },
];

const ALIASES: Record<string, string> = { FETCHING_METADATA: 'RECEIVED', ENRICHING: 'ATTRIBUTING' };

const AUDIO_ALIASES: Record<string, string> = {
  CONNECTING: 'STREAMING',
  READY: 'STREAMING',
  TRANSCRIBED: 'READING',
  RECEIVED: 'READING',
  FETCHING_METADATA: 'READING',
  CANONICALIZING: 'READING',
};

export function stagesOf(job: Pick<Job, 'kind'>): Stage[] {
  return job.kind === 'live' ? LIVE_STAGES : job.kind === 'audio' ? AUDIO_STAGES : STAGES;
}

/** Index into the job's stages of its current one; -1 while it is still queued. */
export function stageIndex(job: Pick<Job, 'status' | 'phase' | 'kind'>): number {
  const stages = stagesOf(job);
  if (job.status === 'COMPLETE') {
    return stages.length;
  }
  if (job.kind === 'audio' || job.kind === 'live') {
    if (!job.phase) {
      return job.status === 'DISPATCHED' ? stages.length - 2 : -1;
    }
    const id = AUDIO_ALIASES[job.phase] ?? job.phase;
    const index = stages.findIndex((stage) => stage.id === id);
    return index === -1 ? 0 : index;
  }
  if (job.status === 'PENDING' || !job.phase) {
    return job.status === 'DISPATCHED' ? 0 : -1;
  }
  const id = ALIASES[job.phase] ?? job.phase;
  const index = stages.findIndex((stage) => stage.id === id);
  return index === -1 ? 0 : index;
}

export type Tone = 'queued' | 'running' | 'success' | 'warning' | 'danger';

export interface JobState {
  label: string;
  tone: Tone;
  /** One line on what happened, for a refused or failed job. */
  detail: string | null;
}

function humanReason(reason: string): string {
  const words = reason.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function jobState(job: Job): JobState {
  switch (job.status) {
    case 'PENDING': {
      const index = job.kind === 'audio' || job.kind === 'live' ? stageIndex(job) : -1;
      return index < 0
        ? { label: 'Queued', tone: 'queued', detail: null }
        : { label: stagesOf(job)[index].label, tone: 'running', detail: null };
    }
    case 'DISPATCHED': {
      const stage = stagesOf(job)[Math.max(0, stageIndex(job))];
      return { label: stage.label, tone: 'running', detail: null };
    }
    case 'COMPLETE':
      if (job.verdict && job.verdict !== 'normalized') {
        return {
          label: 'Refused',
          tone: 'warning',
          detail: job.error?.message ?? humanReason(job.verdict),
        };
      }
      return { label: 'Complete', tone: 'success', detail: null };
    case 'FAILED': {
      const failure = job.failure;
      const reason =
        failure?.error || (failure?.failureClass ? humanReason(failure.failureClass) : null);
      return { label: 'Failed', tone: 'danger', detail: reason };
    }
    default:
      return { label: 'Deleted', tone: 'queued', detail: null };
  }
}

/** What a job was made from; `live` is a session streamed from the microphone. */
export type JobSource = 'document' | 'photo' | 'audio' | 'live';

const PHOTO = /\.(jpe?g|png|heic|heif|webp|gif|tiff?|bmp)$/i;

export function sourceOf(job: Pick<Job, 'kind' | 'filename'>): JobSource {
  if (job.kind === 'live') {
    return 'live';
  }
  if (job.kind === 'audio') {
    return 'audio';
  }
  return PHOTO.test(job.filename ?? '') ? 'photo' : 'document';
}
